const SECTION_SELECTOR = ".section--feature-track";
const TRACK_SELECTOR = "[data-feature-track='track']";
const CAMERA_SELECTOR = "[data-feature-track='camera']";
const FRAME_SELECTOR = "[data-feature-track='frame']";
const CONTENT_SELECTOR = ".feature-track__content";
const LINE_FILL_SELECTOR = "[data-feature-track='item-line-fill']";
const ITEM_SELECTOR = "[data-feature-track='item']";
const DESKTOP_MQ = window.matchMedia("(min-width: 1199px)");

class FeatureTrack {
  constructor(section) {
    this.section = section;
    this.track = section.querySelector(TRACK_SELECTOR);
    this.camera = section.querySelector(CAMERA_SELECTOR);
    this.frame = section.querySelector(FRAME_SELECTOR);
    this.content = section.querySelector(CONTENT_SELECTOR);
    this.items = [...section.querySelectorAll(ITEM_SELECTOR)];
    this.lineFills = this.items
      .map((item) => item.querySelector(LINE_FILL_SELECTOR))
      .filter(Boolean);
    this.scrollTrigger = null;
    this.tween = null;

    this.onMediaChange = this.onMediaChange.bind(this);
    DESKTOP_MQ.addEventListener("change", this.onMediaChange);

    this.init();
  }

  onMediaChange() {
    this.kill();
    this.init();
    if (typeof ScrollTrigger !== "undefined") {
      ScrollTrigger.refresh();
    }
  }

  kill() {
    if (this.scrollTrigger) {
      this.scrollTrigger.kill();
      this.scrollTrigger = null;
    }

    if (this.tween) {
      this.tween.kill();
      this.tween = null;
    }

    gsap.set(this.frame, { clearProps: "transform" });

    this.items.forEach((item) => {
      const fill = item.querySelector(LINE_FILL_SELECTOR);
      if (fill) {
        gsap.set(fill, { clearProps: "transform" });
      }

      gsap.set(item, { clearProps: "opacity,transform" });
    });

    this.items.forEach((item, index) => {
      item.classList.remove("is-active", "is-current");
      if (index === 0) {
        item.classList.add("is-active", "is-current");
      }
    });
  }

  init() {
    if (!this.track || !this.frame || !this.content || !this.items.length) {
      return;
    }

    if (DESKTOP_MQ.matches) {
      this.initHorizontal();
      return;
    }

    this.initVertical();
  }

  getHorizontalDistance() {
    return Math.max(0, this.content.scrollWidth - window.innerWidth);
  }

  getSegmentProgress(progress) {
    const clampedProgress = Math.min(1, Math.max(0, progress));
    return clampedProgress * this.items.length;
  }

  updateMarkerFills(progress) {
    const totalProgress = this.getSegmentProgress(progress);
    const isDesktop = DESKTOP_MQ.matches;

    this.items.forEach((item, index) => {
      const fill = item.querySelector(LINE_FILL_SELECTOR);
      if (!fill) {
        return;
      }

      const segmentProgress = totalProgress - index;
      const fillAmount = Math.min(1, Math.max(0, segmentProgress));

      gsap.set(fill, {
        scaleX: isDesktop ? fillAmount : 1,
        scaleY: isDesktop ? 1 : fillAmount,
        force3D: true,
      });
    });
  }

  updateItemStates(progress) {
    const clampedProgress = Math.min(1, Math.max(0, progress));
    const lastIndex = this.items.length - 1;
    const totalProgress = this.getSegmentProgress(clampedProgress);
    const activeIndex =
      clampedProgress >= 1
        ? lastIndex
        : Math.min(lastIndex, Math.max(0, Math.floor(totalProgress)));

    this.items.forEach((item, index) => {
      const distance = totalProgress - index;
      const isActive = index <= activeIndex;
      const isCurrent = index === activeIndex;

      item.classList.toggle("is-active", isActive);
      item.classList.toggle("is-current", isCurrent);

      const opacity = isActive ? 1 : Math.max(0.35, 1 + distance * 0.5);
      gsap.set(item, { opacity });
    });

    this.updateMarkerFills(clampedProgress);
  }

  handleScrollEnd(self) {
    if (self.progress >= 0.999) {
      this.updateItemStates(1);
    }
  }

  initHorizontal() {
    const getScrollDistance = () => this.getHorizontalDistance();

    this.tween = gsap.to(this.frame, {
      x: () => -getScrollDistance(),
      ease: "none",
      scrollTrigger: {
        trigger: this.section,
        start: "top top",
        end: () => `+=${getScrollDistance()}`,
        scrub: 1,
        pin: this.camera,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => this.updateItemStates(self.progress),
        onLeave: (self) => this.handleScrollEnd(self),
      },
    });

    this.scrollTrigger = this.tween.scrollTrigger;
    this.updateItemStates(0);

    requestAnimationFrame(() => {
      if (typeof ScrollTrigger !== "undefined") {
        ScrollTrigger.refresh();
      }
    });
  }

  initVertical() {
    this.scrollTrigger = ScrollTrigger.create({
      trigger: this.frame,
      start: "top 70%",
      end: "bottom 30%",
      scrub: 0.5,
      invalidateOnRefresh: true,
      onUpdate: (self) => this.updateItemStates(self.progress),
      onLeave: (self) => this.handleScrollEnd(self),
    });

    this.updateItemStates(0);

    requestAnimationFrame(() => {
      if (typeof ScrollTrigger !== "undefined") {
        ScrollTrigger.refresh();
      }
    });
  }
}

function initFeatureTrack() {
  if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  document.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
    new FeatureTrack(section);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initFeatureTrack);
} else {
  initFeatureTrack();
}
